import { Appearance, Box, Button, Icon } from "@blasterjs/core";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import Avatar from "../components/Avatar";
import { StyledDataTable } from "../components/Table";
import TooltipFlyout, { TooltipLink } from "../components/Tooltip";
import Content from "./Content";
import {
  AdjudicationMode,
  AdjudicationParadigm,
  AdminCase,
  CaseAndCounts,
  CaseAndCountsAdminView,
  CaseReaderAssignment,
  formatCaseStatus,
  formatReaderStatus,
  UUID
} from "../models";
import { AppDispatch } from "../store";
import { dataTableTheme } from "../theme";
import ArchiveCaseDialog from "../components/ArchiveCaseDialog";
import PutOnHoldCaseDialog from "../components/PutOnHoldCaseDialog";
import RemoveHoldCaseDialog from "../components/RemoveHoldCaseDialog";
import CaseDialog from "./CaseDialog";
import Timestamp from "../components/Timestamp";

import CaseStatusDialog from "./CaseStatusDialog";
import { useLocalIntStorage } from "../storage";
import { useAppDispatch, useAppSelector } from "../hooks";
import { casesFetch } from "../slices/cases";
import { openEditCaseDialog } from "../slices/caseDialog";
import { openCaseStatusDialog } from "../slices/caseStatusDialog";
import { LoggedInUser, RolePermissions } from "../permissions";
import { openArchiveCaseDialog } from "../slices/archiveCaseDialog";
import { openPutOnHoldCaseDialog } from "../slices/putOnHoldCaseDialog";
import { openRemoveHoldCaseDialog } from "../slices/removeHoldCaseDialog";
import RedLabel from "./RedLabel";

interface Props {
  readonly studyId: UUID;
  // `null` while the study's paradigm is still loading — see CaseDialogState.adjudicationParadigm.
  readonly adjudicationParadigm: AdjudicationParadigm | null;
  readonly adjudicationMode: AdjudicationMode;
  readonly loggedInUser: LoggedInUser;
  readonly userIsReader: boolean;
}

const editCase = (
  histoCase: AdminCase,
  adjudicationParadigm: AdjudicationParadigm | null,
  adjudicationMode: AdjudicationMode,
  dispatch: AppDispatch
) => () =>
  dispatch(
    openEditCaseDialog({
      id: histoCase.id,
      studyId: histoCase.studyId,
      adjudicationParadigm,
      adjudicationMode,
      refreshSingleCaseOnSuccess: false
    })
  );

const editCaseStatus = (histoCase: AdminCase, dispatch: AppDispatch) => () =>
  dispatch(openCaseStatusDialog(histoCase));

const archiveCase = (histoCase: AdminCase, dispatch: AppDispatch) => () =>
  dispatch(openArchiveCaseDialog(histoCase));

const putOnHoldCase = (histoCase: AdminCase, dispatch: AppDispatch) => () =>
  dispatch(openPutOnHoldCaseDialog(histoCase));

const removeHoldCase = (histoCase: AdminCase, dispatch: AppDispatch) => () =>
  dispatch(openRemoveHoldCaseDialog(histoCase));

// The per-reader assignments only exist on the admin/ISC case-list view; readers get the non-admin
// view with no `assignments` field. Narrow the row union to that array (empty for non-admin rows).
const readerAssignments = (row: CaseAndCounts): ReadonlyArray<CaseReaderAssignment> =>
  "assignments" in row ? row.assignments : [];

const ExpandedReaderRows = styled.div`
  padding: 0.8rem 1rem 0.8rem 4rem;
  font-size: 1.4rem;
`;

const ExpandedReaderRow = styled.div`
  padding: 0.2rem 0;
`;

// API-08: expanded content for a case row — one sub-row per Central Reader assignment showing that
// reader's own derived status, distinct from the case-level Status column. `data` is the expanded
// row, matching react-data-table-component's expandableRowsComponent prop shape ({ data: T }).
const ReaderStatusExpander = ({ data }: { readonly data: CaseAndCounts }) => (
  <ExpandedReaderRows>
    {readerAssignments(data).map((assignment: CaseReaderAssignment) => (
      <ExpandedReaderRow key={assignment.assignmentId}>
        {assignment.centralReaderUsername}: {formatReaderStatus(assignment.readerStatus)}
      </ExpandedReaderRow>
    ))}
  </ExpandedReaderRows>
);

const CasesTable = ({
  studyId,
  adjudicationParadigm,
  adjudicationMode,
  loggedInUser,
  userIsReader
}: Props) => {
  const dispatch = useAppDispatch();
  const cases = useAppSelector(state => state.cases.cases);

  useEffect(() => {
    dispatch(casesFetch(studyId));
  }, [studyId]);
  const navigate = useNavigate();
  const userCanEditCaseStatus = loggedInUser.can([RolePermissions.S_CaseListTab_EditCase]);
  const userCanArchiveCases = loggedInUser.can([RolePermissions.IM_CaseListTab_ArchiveCase]);
  const userCanPutOnHoldCases = loggedInUser.can([RolePermissions.IM_CaseListTab_PutoHold]);
  const columns: ReadonlyArray<object> = [
    ...[
      {
        name: "Histo Procedure ID",
        selector: (caseAndCounts: CaseAndCounts) => caseAndCounts.caseWithStatus.histoProcedureId,
        sortable: true,
        width: "19rem"
      },
      {
        name: "Endo Procedure ID",
        width: "19rem",
        selector: (caseAndCounts: CaseAndCounts) => caseAndCounts.caseWithStatus.procId,
        sortable: true
      },
      {
        name: "Created",
        selector: (caseAndCounts: CaseAndCounts) => caseAndCounts.caseWithStatus.createdAt,
        sortable: true,
        width: "12rem",
        format: (caseAndCounts: CaseAndCounts) => (
          <Timestamp date={caseAndCounts.caseWithStatus.createdAt} />
        )
      }
    ],
    ...(loggedInUser.can([RolePermissions.AP_CaseData_ViewSiteID])
      ? [
          {
            name: "Site ID",
            selector: (caseAndCounts: CaseAndCountsAdminView) =>
              caseAndCounts.caseWithStatus.siteId,
            sortable: true,
            width: "14rem"
          }
        ]
      : []),
    ...(loggedInUser.can([RolePermissions.AP_CaseData_ViewSubjectID])
      ? [
          {
            name: "Subject ID",
            selector: (caseAndCounts: CaseAndCountsAdminView) =>
              caseAndCounts.caseWithStatus.subjectId,
            sortable: true,
            width: "14rem"
          }
        ]
      : []),
    ...(loggedInUser.can([RolePermissions.AP_CaseData_ViewVisitID])
      ? [
          {
            name: "Visit ID",
            selector: (caseAndCounts: CaseAndCountsAdminView) =>
              caseAndCounts.caseWithStatus.visitId,
            sortable: true,
            width: "14rem"
          }
        ]
      : []),
    ...[
      ...(loggedInUser.can([RolePermissions.AP_ImageData_ViewImageStatus])
        ? [
            {
              name: "Status",
              minWidth: "17rem",
              selector: (caseAndCounts: CaseAndCounts) =>
                caseAndCounts.caseWithStatus.onHold
                  ? `${caseAndCounts.caseWithStatus.status}:true`
                  : caseAndCounts.caseWithStatus.status,
              sortable: true,
              format: (caseAndCounts: CaseAndCounts) =>
                caseAndCounts.caseWithStatus.onHold ? (
                  <div title="On Hold">
                    {formatCaseStatus(caseAndCounts.caseWithStatus.status)}&nbsp;
                    <RedLabel>ON HOLD</RedLabel>
                  </div>
                ) : (
                  <div>{formatCaseStatus(caseAndCounts.caseWithStatus.status)}&nbsp;</div>
                )
            }
          ]
        : []),
      ...(userIsReader
        ? []
        : [
            {
              name: "QC",
              selector: (caseInfo: CaseAndCountsAdminView) => caseInfo.qc1User,
              sortable: true,
              width: "78px",
              format: (caseInfo: CaseAndCountsAdminView) =>
                caseInfo.qc1User ? <Avatar user={caseInfo.qc1User} /> : "—"
            },
            {
              name: "CR",
              selector: (caseInfo: CaseAndCountsAdminView) => caseInfo.assignedReaders,
              sortable: true,
              width: "80px",
              format: (caseInfo: CaseAndCountsAdminView) => {
                if (caseInfo.assignedReaders) {
                  const firstAssignedReader =
                    0 in caseInfo.assignedReaders ? caseInfo.assignedReaders[0] : null;
                  const secondAssignedReader =
                    1 in caseInfo.assignedReaders ? caseInfo.assignedReaders[1] : null;
                  return (
                    <span>
                      {firstAssignedReader && <Avatar user={firstAssignedReader} />}
                      {secondAssignedReader && (
                        <Box ml="3px" mr="3px" display="inline">
                          <Avatar user={secondAssignedReader} />
                        </Box>
                      )}
                      {caseInfo.assignedReaders.length > 2 ? "…" : null}
                    </span>
                  );
                } else {
                  return "—";
                }
              }
            }
          ]),
      {
        name: "Images",
        selector: (caseAndCounts: CaseAndCounts) => caseAndCounts.numberOfImages,
        width: "90px",
        sortable: true
      }
    ],
    ...(userIsReader
      ? []
      : [
          {
            name: "Queries",
            selector: (caseAndCountsAdminView: CaseAndCountsAdminView) =>
              caseAndCountsAdminView.caseWithStatus.openQueries,
            sortable: true,
            width: "110px"
          }
        ]),
    ...[
      {
        right: true,
        width: "40px",
        cell: (caseAndCounts: CaseAndCounts) =>
          "subjectId" in caseAndCounts.caseWithStatus &&
          loggedInUser.can([RolePermissions.S_CaseListTab_EditCase]) ? (
            <Button data-tooltip={true} appearance={Appearance.MINIMAL}>
              <Icon name="menu" />
              <TooltipFlyout placement={"left-start"}>
                <TooltipLink
                  onClick={editCase(
                    caseAndCounts.caseWithStatus,
                    adjudicationParadigm,
                    adjudicationMode,
                    dispatch
                  )}
                >
                  Edit
                </TooltipLink>
                {caseAndCounts.caseWithStatus.prevWorkflowStatuses.length ? (
                  <TooltipLink onClick={editCaseStatus(caseAndCounts.caseWithStatus, dispatch)}>
                    Revert Status
                  </TooltipLink>
                ) : null}
                {userCanPutOnHoldCases && !caseAndCounts.caseWithStatus.onHold ? (
                  <TooltipLink onClick={putOnHoldCase(caseAndCounts.caseWithStatus, dispatch)}>
                    Put on Hold
                  </TooltipLink>
                ) : null}
                {userCanPutOnHoldCases && caseAndCounts.caseWithStatus.onHold ? (
                  <TooltipLink onClick={removeHoldCase(caseAndCounts.caseWithStatus, dispatch)}>
                    Remove Hold
                  </TooltipLink>
                ) : null}
                {userCanArchiveCases ? (
                  <TooltipLink onClick={archiveCase(caseAndCounts.caseWithStatus, dispatch)}>
                    Archive
                  </TooltipLink>
                ) : null}
              </TooltipFlyout>
            </Button>
          ) : null
      }
    ]
  ];
  const viewCase = (caseAndCounts: CaseAndCounts) =>
    navigate(
      `/studies/${caseAndCounts.caseWithStatus.studyId}/cases/${caseAndCounts.caseWithStatus.id}`
    );

  const [caseRowsPerPage, setCaseRowsPerPage] = useLocalIntStorage("caseRowsPerPage", 20);

  const caseStatusDialog = userCanEditCaseStatus ? <CaseStatusDialog /> : null;
  const caseArchiveDialog = userCanArchiveCases ? <ArchiveCaseDialog /> : null;
  const casePutOnHoldDialog = userCanPutOnHoldCases ? <PutOnHoldCaseDialog /> : null;
  const caseRemoveHoldDialog = userCanPutOnHoldCases ? <RemoveHoldCaseDialog /> : null;
  return (
    <Content isLoading={"isPending" in cases || "isPending" in loggedInUser}>
      {"resource" in cases ? (
        <StyledDataTable
          columns={columns}
          // react-data-table-component keys rows via `keyField` using a flat property lookup (this
          // version does not resolve dotted paths for keyField), and CaseAndCounts has no top-level
          // `id` — its id is at caseWithStatus.id. Without a resolvable key the library falls back to
          // the row's positional index, so the built-in expandable-row state would reattach to
          // whichever case lands at that position after a sort or page change — showing the wrong
          // case's per-reader assignments as expanded. Surface a stable top-level `caseId` and key on
          // it. (Column selectors use the library's separate dotted-path resolver, so are unaffected.)
          data={cases.resource.map(row => ({ ...row, caseId: row.caseWithStatus.id }))}
          keyField="caseId"
          highlightOnHover={true}
          pointerOnHover={true}
          defaultSortField="caseWithStatus.createdAt"
          defaultSortAsc={false}
          sortIcon={<Icon name="caretUp" />}
          className="data-table"
          onRowClicked={viewCase}
          expandableRows={!userIsReader}
          expandableRowsComponent={ReaderStatusExpander}
          expandableRowDisabled={(row: CaseAndCounts) => readerAssignments(row).length === 0}
          noHeader={true}
          pagination={true}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          paginationPerPage={caseRowsPerPage}
          onChangeRowsPerPage={setCaseRowsPerPage}
          customTheme={dataTableTheme}
        />
      ) : null}
      <CaseDialog />
      {caseStatusDialog}
      {caseArchiveDialog}
      {casePutOnHoldDialog}
      {caseRemoveHoldDialog}
    </Content>
  );
};

export default CasesTable;
